import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from 'mobx-react-lite';
import { Box, Typography } from '@mui/material';
import AssetAudioPlayer from '../../common/asset/AssetAudioPlayer';
import AssetDownloadLink from '../../common/AssetDownloadLink';
import AssetPdfPlayer from '../../common/asset/AssetPdfPlayer';
import AssetVideoPlayer from '../../common/asset/AssetVideoPlayer';
import CertificationLesson from './CertificationLesson';
import KnowledgeCheckPanel from '../../course/KnowledgeCheckPanel';
import ParaWithImage from '../../common/paraWithImage/paraWithImage';
import QuizPanel from '../../course/quiz/QuizPanel';
import ScormPlayer from '../../common/scorm/ScormPlayer';
import HtmlPlayer from '../../common/html/HtmlPlayer';
import TypeformWidget from '../../../publisher/components/TypeformWidget';
import { WysiwygViewer } from '../../../publisher/wysiwyg/WysiwygEditor';
const ItemContent = ({ owner, section, progress, showPlayerControls, seekPosition, maxPosition, seekTrigger, onProgress, onEnd, onAttemptComplete, onPause, onDuration, actionButtons, isMicrocast = false, handleNextPrevious, }) => {
    if (section.type === 'video' || section.type === 'microcast' || isMicrocast) {
        const item = section.type === 'video' || isMicrocast ? section.item : section.item.microcast;
        const getAssetImage = () => {
            if (section.type === 'microcast') {
                return section.item?.microcast?.useAsPoster ? (section.item?.microcast?.microcastImage) : section.item?.microcast?.posterImage;
            }
            if (section.item?.posterImage) {
                return section.item?.posterImage;
            }
            return section.item?.asset;
        };
        if (!item) {
            return null;
        }
        return (_jsxs(Box, { children: [item.headline && (_jsx(Typography, { variant: "h4", sx: { p: 2, pt: 0 }, children: item.headline })), _jsx(Box, { sx: { minHeight: { md: '440px', xs: 'auto' } }, children: _jsx(AssetVideoPlayer, { asset: item.asset || item.microcastAsset, assetPoster: getAssetImage(), showControls: showPlayerControls, seekPosition: seekPosition, initialMaxPosition: maxPosition, seekTrigger: seekTrigger, onProgress: onProgress, onEnd: onEnd, onPause: onPause, onDuration: onDuration, handleNextPrevious: handleNextPrevious, disableSeekAhead: section.item.required, item: owner }) }), actionButtons, (item?.content?.slate || item?.content?.s3) && (_jsx(Box, { className: "slate", sx: { p: 2 }, children: _jsx(WysiwygViewer, { id: "content", value: item.content.slate }) }))] }, owner?.enableAutoplay ? owner?.id : item.id));
    }
    if (section.type === 'scorm') {
        return (_jsx(Box, { sx: { height: '500px', width: '100%' }, children: _jsx(ScormPlayer, { itemType: "courseSection", item: section.item, itemProgress: progress, course: owner }) }));
    }
    if (section.type === 'html') {
        return (_jsxs(Box, { sx: { minHeight: '300px' }, children: [section.item.headline && (_jsx(Typography, { variant: "h4", sx: { p: 2 }, children: section.item.headline })), _jsx(HtmlPlayer, { item: section.item, course: owner, onEnd: onEnd })] }));
    }
    if (section.type === 'pdf') {
        return (_jsxs(Box, { sx: { overflow: 'hidden' }, children: [section.item.headline && (_jsx(Typography, { variant: "h4", sx: { p: 2 }, children: section.item.headline })), _jsx(Box, { sx: {
                        position: 'relative',
                        width: '100%',
                        height: '45rem',
                        maxHeight: 'calc(100vh - 300px)',
                        // paddingTop: '56.25%', // 16:9 ratio
                        overflow: 'auto',
                    }, children: _jsx(AssetPdfPlayer, { asset: section.item.asset, assetPoster: section.item.posterImage, showPlayerControls: showPlayerControls, seekPosition: seekPosition, onProgress: onProgress, onEnd: onEnd, onPause: onPause, onDuration: onDuration }) }), actionButtons, section.item?.content?.slate && (_jsx(Box, { className: "slate", sx: { p: 2 }, children: _jsx(WysiwygViewer, { id: "content", value: section.item.content.slate }) }))] }, section.item.id));
    }
    if (section.type === 'audio') {
        return (_jsxs(Box, { children: [_jsxs(Box, { sx: {
                        height: { md: '580px', xs: 'auto' },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }, children: [_jsx(ParaWithImage, { text: section.item.headline, asset: section.item.posterImage, courseItem: section }), _jsx(AssetAudioPlayer, { asset: section.item.asset, seekPosition: seekPosition, onProgress: onProgress, onEnd: onEnd, playerStyle: {
                                padding: '16px 0',
                            } })] }), actionButtons, section.item?.content?.slate && (_jsx(Box, { className: "slate", sx: { p: 2 }, children: _jsx(WysiwygViewer, { id: "content", value: section.item.content.slate }) }))] }, section.item.id));
    }
    if (section.type === 'application') {
        return (_jsxs(Box, { children: [section.item.headline && (_jsx(Typography, { variant: "h4", sx: { p: 2 }, children: section.item.headline })), _jsx(AssetPdfPlayer, { asset: section.item.asset, assetPoster: section.item.posterImage, showPlayerControls: showPlayerControls, seekPosition: seekPosition, onProgress: onProgress, onEnd: onEnd, onPause: onPause, onDuration: onDuration }), actionButtons, section.item?.content?.slate && (_jsx(Box, { className: "slate", sx: { p: 2 }, children: _jsx(WysiwygViewer, { id: "content", value: section.item.content.slate }) }))] }, section.item.id));
    }
    if (section.type === 'text') {
        onEnd();
        return (_jsxs(Box, { children: [_jsx(Box, { children: section.item.headline }), actionButtons] }, section.item.id));
    }
    if (section.type === 'article') {
        onEnd();
        return (_jsxs(Box, { children: [_jsx(Box, { children: section.item.headline }), section.item?.content?.slate && (_jsx(Box, { className: "slate", sx: { p: 2 }, children: _jsx(WysiwygViewer, { id: "content", value: section.item.content.slate }) })), actionButtons] }, section.item.id));
    }
    if (section.type === 'download') {
        return (_jsxs(Box, { children: [_jsx(ParaWithImage, { text: section.item.headline, asset: section.item.posterImage, courseItem: section }), _jsx(Box, { sx: { p: 2 }, children: _jsx(Typography, { sx: {
                            fontSize: 'subtitle1.fontSize',
                            fontWeight: 'fontWeightBold',
                        }, className: "description", children: _jsx(AssetDownloadLink, { name: section.name, asset: section.item.asset, onDownload: onEnd }) }) }), section.item?.content?.slate && (_jsx(Box, { className: "slate", sx: { p: 2 }, children: _jsx(WysiwygViewer, { id: "content", value: section.item.content.slate }) }))] }));
    }
    if (section.type === 'quiz') {
        return (_jsxs(Box, { children: [section.item.headline && (_jsx(Typography, { variant: "h4", sx: { p: 2 }, children: section.item.headline })), _jsx(QuizPanel, { courseItem: section, progress: progress, onEnd: onEnd, onAttemptComplete: onAttemptComplete })] }, section.item.id));
    }
    if (section.type === 'knowledgeCheck') {
        return (_jsxs(Box, { children: [section.item.headline && (_jsx(Typography, { variant: "h4", sx: { p: 2 }, children: section.item.headline })), _jsx(KnowledgeCheckPanel, { courseItem: section, progress: progress, onEnd: onEnd })] }, section.item.id));
    }
    if (section.type === 'block') {
        return (_jsx(Box, { children: section?.item?.subItems?.map((subItem) => (_jsx(ItemContent, { section: subItem, showPlayerControls: showPlayerControls, seekPosition: seekPosition, maxPosition: maxPosition, onProgress: onProgress, onEnd: onEnd, handleNextPrevious: handleNextPrevious }, subItem.id))) }));
    }
    if (section.type === 'certification') {
        return (_jsx(Box, { children: _jsx(CertificationLesson, { courseItem: section.item, progress: progress }) }));
    }
    if (section.type === 'typeform') {
        return (_jsx(Box, { children: _jsx(TypeformWidget, { formID: section.item.typeformId, formHeight: 500 }) }, section.item.typeformId));
    }
    return null;
    // return (
    //   <div>
    //     Component Type not recognized:
    //     {section.type}
    //   </div>
    // );
};
ItemContent.defaultProps = {
    owner: undefined,
    isMicrocast: false,
    progress: undefined,
    seekTrigger: undefined,
    maxPosition: undefined,
    onPause: () => { },
    onDuration: () => { },
    actionButtons: undefined,
    onAttemptComplete: undefined,
};
export default observer(ItemContent);
//# sourceMappingURL=ItemContent.js.map